import {
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { DepartmentsListResult } from "@/modules/departments/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { RowActions } from "@/shared/ui/RowActions";
import { useDeleteDepartment } from "../api/queries";

type DepartmentRow = DepartmentsListResult["data"][number];

type DepartmentsTableProps = {
  data: DepartmentRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (department: DepartmentRow) => void;
  onViewPositions: (department: DepartmentRow) => void;
};

export function DepartmentsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onViewPositions,
}: DepartmentsTableProps) {
  const { has } = usePermissions();
  const nav = useNavigate();
  const deleteDepartment = useDeleteDepartment();

  const columns: TanstackReactTable.ColumnDef<DepartmentRow>[] = [
    createSortableColumn<DepartmentRow>("name", "ຊື່ພະແນກ", { size: 200 }),
    {
      id: "managerName",
      header: "ຫົວໜ້າພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.managerName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "positionCount",
      header: "ຈໍານວນຕໍາແໜ່ງ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.positionCount || 0}</span>
      ),
      size: 120,
    },
    {
      id: "employeeCount",
      header: "ຈໍານວນພະນັກງານ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.employeeCount || 0}</span>
      ),
      size: 120,
    },
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: DepartmentRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
        }[] = [];

        if (has(Permissions.departments.read))
          actions.push({
            label: "ເບິ່ງຕໍາແໜ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onViewPositions(row.original),
          });

        if (has(Permissions.departments.update))
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });

        if (has(Permissions.departments.delete))
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const department = row.original;
              const positionCount = department.positionCount || 0;
              const employeeCount = department.employeeCount || 0;
              const message =
                positionCount > 0 || employeeCount > 0
                  ? `ພະແນກນີ້ມີຕໍາແໜ່ງ ${positionCount} ຕໍາແໜ່ງ ແລະ ພະນັກງານ ${employeeCount} ຄົນ. ການລຶບຈະລຶບທຸກຢ່າງທີ່ກ່ຽວຂ້ອງ. ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ?`
                  : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບພະແນກນີ້?";
              const ok = await confirm({
                title: "ລຶບພະແນກ",
                description: message,
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok)
                toast.promise(deleteDepartment.run(department.id), {
                  loading: "ກໍາລັງລຶບພະແນກ...",
                  success: "ລຶບພະແນກສໍາເລັດ",
                  error: "ລຶບພະແນກລົ້ມເຫຼວ",
                });
            },
          });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<DepartmentRow, unknown>
      noDataMessage="ບໍ່ພົບພະແນກ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
